export interface SpreadsheetRow {
  name: string;
  description: string;
  filename?: string;
  originalPath?: string;
  [key: string]: any; // Allow additional columns
}

export interface ParsedSpreadsheet {
  rows: SpreadsheetRow[];
  headers: string[];
  errors: string[];
}

/**
 * Parse CSV content and extract video metadata
 */
export const parseCSV = (csvContent: string): ParsedSpreadsheet => {
  const errors: string[] = [];
  const lines = csvContent.trim().split('\n');
  
  if (lines.length < 2) {
    errors.push('CSV must have at least a header row and one data row');
    return { rows: [], headers: [], errors };
  }

  // Parse headers
  const headers = parseCSVLine(lines[0]);
  
  // Detect column mappings - be flexible with column names
  const columnMappings = detectColumnMappings(headers);
  
  if (!columnMappings.name && !columnMappings.label) {
    errors.push('Could not find name/label column. Expected columns: name, label, title, caption');
  }
  
  if (!columnMappings.description) {
    errors.push('Could not find description column. Expected: description, caption, summary');
  }

  const rows: SpreadsheetRow[] = [];
  
  // Parse data rows
  for (let i = 1; i < lines.length; i++) {
    if (lines[i].trim() === '') continue; // Skip empty lines
    
    try {
      const values = parseCSVLine(lines[i]);
      if (values.length === 0) continue;
      
      const row: SpreadsheetRow = {
        name: '',
        description: '',
        originalPath: '',
        filename: ''
      };

      // Map values to standardized fields
      headers.forEach((header, index) => {
        const value = values[index] || '';
        row[header] = value; // Keep original columns
        
        // Map to standardized fields
        if (columnMappings.name === header || columnMappings.label === header) {
          row.name = value.trim();
        }
        if (columnMappings.description === header) {
          row.description = value.trim();
        }
        if (columnMappings.path === header || columnMappings.videoFile === header) {
          row.originalPath = value.trim();
          // Extract filename from path
          row.filename = extractFilenameFromPath(value.trim());
        }
      });

      // Validate required fields
      if (!row.name && !row.originalPath) {
        errors.push(`Row ${i + 1}: Missing both name and file path`);
        continue;
      }

      rows.push(row);
    } catch (error) {
      errors.push(`Row ${i + 1}: Error parsing - ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  return { rows, headers, errors };
};

/**
 * Parse a single CSV line, handling quoted values and commas
 */
const parseCSVLine = (line: string): string[] => {
  const result: string[] = [];
  let current = '';
  let inQuotes = false;
  let i = 0;

  while (i < line.length) {
    const char = line[i];
    
    if (char === '"') {
      if (inQuotes && line[i + 1] === '"') {
        // Escaped quote
        current += '"';
        i += 2;
      } else {
        inQuotes = !inQuotes;
        i++;
      }
    } else if (char === ',' && !inQuotes) {
      result.push(current);
      current = '';
      i++;
    } else {
      current += char;
      i++;
    }
  }
  
  result.push(current);
  return result;
};

/**
 * Detect column mappings from headers
 */
const detectColumnMappings = (headers: string[]) => {
  const mappings: { [key: string]: string } = {};
  
  headers.forEach(header => {
    const lower = header.toLowerCase().trim();
    
    // Name/Title mappings
    if (['name', 'title', 'label', 'caption'].includes(lower)) {
      if (!mappings.name) mappings.name = header;
      if (lower === 'label') mappings.label = header;
    }
    
    // Description mappings
    if (['description', 'summary', 'caption', 'desc'].includes(lower)) {
      if (!mappings.description) mappings.description = header;
    }
    
    // Path mappings
    if (['path', 'filepath', 'videofile', 'video_file', 'file', 'url', 'link'].includes(lower)) {
      if (!mappings.path) mappings.path = header;
      if (lower === 'videofile') mappings.videoFile = header;
    }
  });
  
  return mappings;
};

/**
 * Extract filename from a path or URL
 */
const extractFilenameFromPath = (path: string): string => {
  if (!path) return '';
  
  // Handle URLs
  try {
    const url = new URL(path);
    const pathname = url.pathname;
    const filename = pathname.split('/').pop() || '';
    return filename;
  } catch {
    // Not a valid URL, treat as path
  }
  
  // Handle regular file paths
  const parts = path.split(/[/\\]/);
  return parts[parts.length - 1] || '';
};

/**
 * Match spreadsheet rows with uploaded files
 */
export const matchFilesWithSpreadsheet = (
  files: File[],
  spreadsheetRows: SpreadsheetRow[]
): { matches: Array<{ file: File; row: SpreadsheetRow }>; unmatched: File[] } => {
  const matches: Array<{ file: File; row: SpreadsheetRow }> = [];
  const unmatched: File[] = [];
  const usedRows = new Set<number>();
  
  files.forEach(file => {
    let matched = false;
    
    // Try different matching strategies
    for (let i = 0; i < spreadsheetRows.length; i++) {
      if (usedRows.has(i)) continue;
      
      const row = spreadsheetRows[i];
      
      // Strategy 1: Exact filename match
      if (row.filename && file.name === row.filename) {
        matches.push({ file, row });
        usedRows.add(i);
        matched = true;
        break;
      }
      
      // Strategy 2: Filename without extension match
      const fileBasename = file.name.replace(/\.[^/.]+$/, '');
      const rowBasename = row.filename ? row.filename.replace(/\.[^/.]+$/, '') : '';
      if (rowBasename && fileBasename.toLowerCase() === rowBasename.toLowerCase()) {
        matches.push({ file, row });
        usedRows.add(i);
        matched = true;
        break;
      }
      
      // Strategy 3: Name similarity match (fuzzy matching)
      if (row.name && isNameSimilar(file.name, row.name)) {
        matches.push({ file, row });
        usedRows.add(i);
        matched = true;
        break;
      }
    }
    
    if (!matched) {
      unmatched.push(file);
    }
  });
  
  return { matches, unmatched };
};

/**
 * Check if two names are similar (basic fuzzy matching)
 */
const isNameSimilar = (filename: string, rowName: string): boolean => {
  const normalize = (str: string) => 
    str.toLowerCase()
       .replace(/[^a-z0-9]/g, '')
       .trim();
  
  const normalizedFilename = normalize(filename);
  const normalizedRowName = normalize(rowName);
  
  // Check if one contains the other
  return normalizedFilename.includes(normalizedRowName) || 
         normalizedRowName.includes(normalizedFilename);
};

/**
 * Read file as text (for CSV files)
 */
export const readFileAsText = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => resolve(e.target?.result as string);
    reader.onerror = (e) => reject(new Error('Failed to read file'));
    reader.readAsText(file);
  });
}; 